import type { TemplateMeta } from '../../api/templates';

/** Curated landing-page showcase previews (uploaded presentation assets). */
export const SHOWCASE_BUSINESS_PREVIEW = '/assets/template-previews/business/professional-classic.png';
export const SHOWCASE_ARTIST_PREVIEW = '/assets/template-previews/artist/aurora-gradient-exact.png';

/** Curated gallery preview images (presentation assets mapped to real template IDs). */
export const TEMPLATE_PREVIEW_ASSETS: Record<string, string> = {
  // Business — user-provided clean previews (Aug 2026)
  't_static_1': '/assets/template-previews/business/professional-classic.png',
  't_static_2': '/assets/template-previews/business/elegant-looking.png',
  't_static_3': '/assets/template-previews/business/modern-and-clean.png',
  't_static_5': '/assets/template-previews/business/classic-modular.png',
  't_1760711434756': '/assets/template-previews/business/golden-view.png',
  't_1760657746541': '/assets/template-previews/business/classic-modular.png',
  't_1760657746537': '/assets/template-previews/business/elegant-looking.png',
  't_1760657746536': '/assets/template-previews/business/modern-and-clean.png',
  't_1760653763366': '/assets/template-previews/business/golden-view.png',
  'professional-classic': '/assets/template-previews/business/professional-classic.png',
  'elegant-looking': '/assets/template-previews/business/elegant-looking.png',
  'modern-clean': '/assets/template-previews/business/modern-and-clean.png',
  'classic-modular': '/assets/template-previews/business/classic-modular.png',
  'golden-view': '/assets/template-previews/business/golden-view.png',
  // Artist — user-provided clean previews (Aug 2026)
  't_1786708162234_z6kq': '/assets/template-previews/artist/aurora-gradient-exact.png',
  't_1786708162473_v6lx': '/assets/template-previews/artist/blue-artist-exact.png',
  't_1786708162562_ad0v': '/assets/template-previews/artist/dark-artist-exact.png',
  't_1786708162552_kgs3': '/assets/template-previews/artist/orange-classic-exact.png',
  't_1760690814973': '/assets/template-previews/artist/blue-artist-exact.png',
  't_1760711848336': '/assets/template-previews/artist/orange-classic-exact.png',
  'classic-modular-e8a978c9': '/assets/template-previews/artist/aurora-gradient-exact.png',
  'classic-modular-8ef1ef16': '/assets/template-previews/artist/blue-artist-exact.png',
  'classic-modular-dark-9cc5d1cc-9b6c7b98': '/assets/template-previews/artist/dark-artist-exact.png',
  'classic-modular-oclassic-11055cd5-82f893b6': '/assets/template-previews/artist/orange-classic-exact.png',
  'aurora-gradient': '/assets/template-previews/artist/aurora-gradient-exact.png',
  'blue-artist': '/assets/template-previews/artist/blue-artist-exact.png',
  'dark-artist': '/assets/template-previews/artist/dark-artist-exact.png',
  'orange-classic': '/assets/template-previews/artist/orange-classic-exact.png',
};

const NAME_FRAGMENTS: { match: string; asset: string }[] = [
  { match: 'elegant looking', asset: '/assets/template-previews/business/elegant-looking.png' },
  { match: 'elegant', asset: '/assets/template-previews/business/elegant-looking.png' },
  { match: 'modern and clean', asset: '/assets/template-previews/business/modern-and-clean.png' },
  { match: 'modern', asset: '/assets/template-previews/business/modern-and-clean.png' },
  { match: 'classic modular', asset: '/assets/template-previews/business/classic-modular.png' },
  { match: 'modular', asset: '/assets/template-previews/business/classic-modular.png' },
  { match: 'golden view', asset: '/assets/template-previews/business/golden-view.png' },
  { match: 'golden', asset: '/assets/template-previews/business/golden-view.png' },
  { match: 'professional classic', asset: '/assets/template-previews/business/professional-classic.png' },
  { match: 'professional', asset: '/assets/template-previews/business/professional-classic.png' },
  { match: 'aurora gradient', asset: '/assets/template-previews/artist/aurora-gradient-exact.png' },
  { match: 'aurora', asset: '/assets/template-previews/artist/aurora-gradient-exact.png' },
  { match: 'blue artist', asset: '/assets/template-previews/artist/blue-artist-exact.png' },
  { match: 'dark artist', asset: '/assets/template-previews/artist/dark-artist-exact.png' },
  { match: 'orange classic', asset: '/assets/template-previews/artist/orange-classic-exact.png' },
];

/**
 * Returns the best gallery preview URL for a template.
 * Prefers curated presentation assets; falls back to server previewUrl.
 */
export function getTemplatePreviewUrl(template: TemplateMeta | null | undefined): string {
  if (!template) return '';
  const id = String(template.id || '').trim();
  if (id && TEMPLATE_PREVIEW_ASSETS[id]) return TEMPLATE_PREVIEW_ASSETS[id];

  const componentKey = String(template.componentKey || '').toLowerCase().trim();
  if (componentKey && TEMPLATE_PREVIEW_ASSETS[componentKey]) return TEMPLATE_PREVIEW_ASSETS[componentKey];
  // Dynamic bundles often use componentKey prefixes like classic-modular-xxxx
  for (const [key, asset] of Object.entries(TEMPLATE_PREVIEW_ASSETS)) {
    if (componentKey.startsWith(key) && key.includes('-')) return asset;
  }

  const name = String(template.name || '').toLowerCase();
  for (const { match, asset } of NAME_FRAGMENTS) {
    if (name.includes(match)) return asset;
  }

  return String(template.previewUrl || '').trim();
}

/** Business templates active in TCards still awaiting curated preview images. */
export const MISSING_BUSINESS_PREVIEW_NAMES = [] as const;
